import React, { useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Skeleton from '@mui/material/Skeleton';
import { useOutletContext } from 'react-router-dom';
import axios from 'axios';
import { DateTime } from 'luxon';
import Kpi from './Kpi';
import LineChart from '../charts/LineChart';
import { useTheme } from '@mui/material/styles';
import { trackWidgetView } from '../../helpers/Analytics';

const GamEcpm = (props) => {
  const { setDisplayError } = useOutletContext();
  const { filtersReady } = useOutletContext();
  const [kpiData, setKpiData] = useState(null);
  const [fetchingKpiData, setfetchingKpiData] = useState(false);
  const [loadingKpis, setLoadingKpis] = useState(true);
  const [chartData, setChartData] = useState(null);

  const theme = useTheme();

  const fetchKpiData = () => {
    if (props.apiRequestOptions) {
      setLoadingKpis(true);
      setfetchingKpiData(true);

      var oParams = new URLSearchParams({
        hostname: props.domains,
        startDate: props.startDate.toISODate(),
        endDate: props.endDate.toISODate(),
      });

      var oAxiosOptions = {
        headers: props.apiRequestOptions.headers,
        params: oParams,
      };

      axios.get('/api/v1/revenue/gam_ecpm', oAxiosOptions).then(
        (response) => {
          setKpiData(response.data);

          var daysAgo = Math.ceil(props.startDate.diffNow('days').days);
          var totalDays = Math.ceil(props.startDate.diff(props.endDate, 'days').days) - 1;
          var dates = null;
          var labels = [];

          // get dates as Luxon date objects and create labels
          if (daysAgo === -7) {
            dates = response.data.ecpm_period_stats.map((d) => DateTime.fromISO(d.start_date));
            labels = dates.map((d) => d.weekdayShort);
          } else if (totalDays <= -365) {
            dates = response.data.ecpm_period_stats.map((d) => DateTime.fromISO(d.start_date));
            labels = dates.map((d) => d.monthShort);
          } else {
            dates = response.data.ecpm_period_stats.map((d) => DateTime.fromISO(d.start_date));
            // if labels all share the same year, remove the /YYYY
            if (dates[0] && dates[0].year === dates[dates.length - 1].year) {
              labels = dates.map((d) => d.toLocaleString({ month: 'numeric', day: 'numeric' }));
            } else {
              labels = dates.map((d) => d.toLocaleString());
            }
          }

          setChartData({
            labels,
            datasets: [
              {
                label: 'eCPM',
                data: response.data.ecpm_period_stats.map((d) => d.period_ecpm),
                fill: true,
                borderColor: theme.palette.chart.green.main,
                backgroundColor: theme.palette.chart.green.o50,
                tension: 0.2,
              },
            ],
          });

          setfetchingKpiData(false);
          setLoadingKpis(false);
          trackWidgetView({ name: 'GAM eCPM' });
        },
        (error) => {
          setDisplayError(error);
        }
      );
    }
  };

  useEffect(() => {
    if (filtersReady && !fetchingKpiData && props.domains) {
      fetchKpiData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.domains, props.startDate, props.endDate]);

  useEffect(() => {
    props.setLoadingGamEcpm(fetchingKpiData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchingKpiData]);

  return (
    <Grid container spacing={{ xs: 0, sm: 4 }}>
      <Grid item xs={12}>
        {!loadingKpis && kpiData && ['total_ecpm', 'total_ecpm_diff_pct', 'days_diff'].every((key) => key in kpiData) ? (
          <Kpi
            type="money"
            value={kpiData.total_ecpm}
            maximumFractionDigits={2}
            label="GAM eCPM"
            diff={kpiData.total_ecpm_diff_pct}
            period={kpiData.days_diff}
          />
        ) : (
          <Skeleton variant="rectangular" height={110} />
        )}
      </Grid>
      <Grid item xs={12} sm sx={{ mt: 2 }}>
        {!loadingKpis && chartData ? (
          <Box sx={{ maxWidth: { xl: '95%' } }}>
            <LineChart data={chartData} type="vertical" labely="eCPM" formaty="money" maxticksx={7}></LineChart>
          </Box>
        ) : (
          <Skeleton variant="rectangular" height={250} />
        )}
      </Grid>
    </Grid>
  );
};

export default GamEcpm;
